import { BgColorsOutlined, BorderOutlined, BulbOutlined, FireOutlined, MoonOutlined, ReloadOutlined } from '@ant-design/icons';
import { useIntl, useModel } from '@umijs/max';
import { Button, Modal, message, Space, Typography } from 'antd';
import { createStyles } from 'antd-style';
import React, { useEffect, useState } from 'react';

const { Title, Text } = Typography;

type ThemeMode = 'light' | 'dark' | 'darkOrange' | 'minimal' | 'auto';
type NavTheme = 'light' | 'realDark';

interface ThemeOption {
  key: ThemeMode;
  label: string;
  description: string;
  icon: React.ReactNode;
  navTheme: NavTheme;
}

const THEME_STORAGE_KEY = 'er-console-theme';

const useStyles = createStyles(({ token }) => ({
  themeButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    fontSize: '18px',
    color: 'inherit',
    cursor: 'pointer',
    borderRadius: token.borderRadius,
    transition: 'all 0.3s',
    '&:hover': {
      backgroundColor: token.colorBgTextHover,
    },
  },
  modalContent: {
    padding: '16px 0',
  },
  themeOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadius,
    cursor: 'pointer',
    transition: 'all 0.3s',
    marginBottom: '12px',
    position: 'relative',
    '&:hover': {
      borderColor: token.colorPrimary,
    },
    '&.selected': {
      borderColor: token.colorPrimary,
      backgroundColor: token.colorPrimaryBg,
    },
    // 黑白极简主题的特殊选中效果
    '&.selected.minimal-theme': {
      borderColor: '#000000',
      borderWidth: '2px',
      backgroundColor: 'transparent',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
        borderRadius: token.borderRadius,
        pointerEvents: 'none',
      },
    },
  },
  themeInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  themeIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
  },
  lightIcon: {
    backgroundColor: '#f5f5f5',
    color: '#1890ff',
  },
  darkIcon: {
    backgroundColor: '#141414',
    color: '#fff',
  },
  darkOrangeIcon: {
    backgroundColor: '#141414',
    color: '#ff7a00',
  },
  minimalIcon: {
    backgroundColor: '#f5f5f5',
    color: '#000000',
  },
  themeDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  colorPreview: {
    display: 'flex',
    gap: '6px',
    alignItems: 'flex-start',
    marginTop: '4px',
  },
  colorDot: {
    width: '20px',
    height: '20px',
    borderRadius: '2px',
    border: '1px solid rgba(0,0,0,0.1)',
  },
  resetButton: {
    marginTop: '16px',
  },
}));

const ThemeSelector: React.FC = () => {
  const { styles, cx } = useStyles();
  const [visible, setVisible] = useState(false);
  const intl = useIntl();
  const { initialState, setInitialState } = useModel('@@initialState');
  const [currentThemeMode, setCurrentThemeMode] = useState<ThemeMode>('auto');

  // 检测系统主题
  const getSystemTheme = (): NavTheme => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'realDark' : 'light';
    }
    return 'light';
  };

  // 根据主题模式获取实际的navTheme
  const getNavThemeFromMode = (mode: ThemeMode): NavTheme => {
    switch (mode) {
      case 'light':
        return 'light';
      case 'dark':
      case 'darkOrange':
        return 'realDark';
      case 'minimal':
        return 'light'; // 黑白极简使用light作为基础，通过自定义样式实现黑色按钮
      case 'auto':
        return getSystemTheme();
      default:
        return 'light';
    }
  };

  // 保存主题到本地存储
  const saveThemeToStorage = (theme: ThemeMode) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  };

  // 从本地存储读取主题
  const loadThemeFromStorage = (): ThemeMode => {
    try {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      if (saved && ['light', 'dark', 'darkOrange', 'minimal', 'auto'].includes(saved)) {
        return saved as ThemeMode;
      }
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
    }
    return 'auto'; // 默认使用自动模式
  };

  // 应用主题
  const applyTheme = async (mode: ThemeMode, showMessage: boolean = true) => {
    const navTheme = getNavThemeFromMode(mode);
    const themeConfig = getThemeConfig(mode);
    
    if (initialState) {
      await setInitialState({
        ...initialState,
        settings: {
          ...initialState.settings,
          navTheme,
          colorPrimary: themeConfig.colorPrimary,
        },
      });
    }
    
    setCurrentThemeMode(mode);
    saveThemeToStorage(mode);
    
    // 只在用户主动切换时显示提示
    if (showMessage) {
      message.success({
        content: intl.formatMessage({ 
          id: 'app.theme.switch.success', 
          defaultMessage: '主题切换成功！请刷新页面以获得最佳体验。' 
        }),
        duration: 4,
      });
    }
  };

  // 获取主题配置
  const getThemeConfig = (mode: ThemeMode) => {
    switch (mode) {
      case 'darkOrange':
        return {
          colorPrimary: '#ff7a00',
          token: {
            colorPrimary: '#ff7a00',
            colorPrimaryHover: '#ff9533',
            colorPrimaryActive: '#e66a00',
            colorBgContainer: '#141414',
            colorBgElevated: '#1f1f1f',
            colorText: '#ffffff',
            colorTextSecondary: '#cccccc',
            colorBorder: '#434343',
            colorBorderSecondary: '#303030',
          },
        };
      case 'minimal':
        return {
          colorPrimary: '#000000',
          token: {
            colorPrimary: '#000000',
            colorPrimaryHover: '#262626',
            colorPrimaryActive: '#000000',
          },
        };
      default:
        return {
          colorPrimary: '#1890ff',
          token: {},
        };
    }
  };

  // 监听系统主题变化
  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleSystemThemeChange = () => {
        if (currentThemeMode === 'auto') {
          applyTheme('auto');
        }
      };

      mediaQuery.addEventListener('change', handleSystemThemeChange);
      
      return () => {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      };
    }
    return undefined;
  }, [currentThemeMode]);

  // 初始化主题
  useEffect(() => {
    const savedTheme = loadThemeFromStorage();
    applyTheme(savedTheme, false); // 初始化时不显示提示
  }, []);

  const themeOptions: ThemeOption[] = [
    {
      key: 'light',
      label: intl.formatMessage({ id: 'app.theme.light', defaultMessage: '亮色主题' }),
      description: intl.formatMessage({ id: 'app.theme.light.description', defaultMessage: '保持现有的浅色 / 深色设计。' }),
      icon: <BulbOutlined />,
      navTheme: 'light',
    },
    {
      key: 'dark',
      label: intl.formatMessage({ id: 'app.theme.dark', defaultMessage: '黑暗主题' }),
      description: intl.formatMessage({ id: 'app.theme.dark.description', defaultMessage: '深邃黑色，温润白色与活力橙色的对比配色。启用后页面将会自动检测深色模式。' }),
      icon: <MoonOutlined />,
      navTheme: 'realDark',
    },
    {
      key: 'darkOrange',
      label: intl.formatMessage({ id: 'app.theme.darkOrange', defaultMessage: '黑橙主题' }),
      description: intl.formatMessage({ id: 'app.theme.darkOrange.description', defaultMessage: '深邃黑色背景，清晰白色文字与活力橙色按钮的对比配色。个性化的暗色主题选择。' }),
      icon: <FireOutlined />,
      navTheme: 'realDark',
    },
    {
      key: 'minimal',
      label: intl.formatMessage({ id: 'app.theme.minimal', defaultMessage: '黑白极简' }),
      description: intl.formatMessage({ id: 'app.theme.minimal.description', defaultMessage: '简洁白色背景，深色文字与纯黑按钮的极简配色。追求简约美学的主题选择。' }),
      icon: <BorderOutlined />,
      navTheme: 'light',
    },
  ];

  const currentTheme = initialState?.settings?.navTheme || 'light';

  const handleThemeChange = (themeOption: ThemeOption) => {
    applyTheme(themeOption.key);
  };

  const handleResetToDefault = () => {
    applyTheme('auto');
  };

  return (
    <>
      <div
        className={styles.themeButton}
        onClick={() => setVisible(true)}
        title={intl.formatMessage({ id: 'app.theme.selector', defaultMessage: '主题切换' })}
      >
        <BgColorsOutlined />
      </div>

      <Modal
        title={intl.formatMessage({ id: 'app.theme.settings', defaultMessage: '主题设置' })}
        open={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={500}
      >
        <div className={styles.modalContent}>
          <Text type="secondary">
            {intl.formatMessage({ 
              id: 'app.theme.settings.description', 
              defaultMessage: '在这里选择页面主题，体验不同的色彩风格～' 
            })}
          </Text>
          
          <div style={{ marginTop: '24px' }}>
            {themeOptions.map((option) => {
              // 修复选中逻辑：auto模式时不应该选中任何具体主题
              const isSelected = currentThemeMode === option.key;
              
              return (
                <div
                  key={option.key}
                  className={cx(
                    styles.themeOption,
                    isSelected && 'selected',
                    isSelected && option.key === 'minimal' && 'minimal-theme'
                  )}
                  onClick={() => handleThemeChange(option)}
                >
                  <div className={styles.themeInfo}>
                  <div className={cx(
                    styles.themeIcon, 
                    option.key === 'light' ? styles.lightIcon : 
                    option.key === 'darkOrange' ? styles.darkOrangeIcon :
                    option.key === 'minimal' ? styles.minimalIcon :
                    styles.darkIcon
                  )}>
                    {option.icon}
                  </div>
                  <div className={styles.themeDetails}>
                    <Title level={5} style={{ margin: 0 }}>
                      {option.label}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {option.description}
                    </Text>
                  </div>
                </div>
                <div className={styles.colorPreview}>
                   {option.key === 'light' ? (
                     <>
                       <div className={styles.colorDot} style={{ backgroundColor: '#ffffff' }} />
                       <div className={styles.colorDot} style={{ backgroundColor: '#000000' }} />
                       <div className={styles.colorDot} style={{ backgroundColor: '#1890ff' }} />
                     </>
                   ) : option.key === 'darkOrange' ? (
                     <>
                       <div className={styles.colorDot} style={{ backgroundColor: '#141414' }} />
                       <div className={styles.colorDot} style={{ backgroundColor: '#ffffff' }} />
                       <div className={styles.colorDot} style={{ backgroundColor: '#ff7a00' }} />
                     </>
                   ) : option.key === 'minimal' ? (
                     <>
                       <div className={styles.colorDot} style={{ backgroundColor: '#ffffff' }} />
                       <div className={styles.colorDot} style={{ backgroundColor: '#000000' }} />
                       <div className={styles.colorDot} style={{ backgroundColor: '#000000' }} />
                     </>
                   ) : (
                     <>
                       <div className={styles.colorDot} style={{ backgroundColor: '#141414' }} />
                       <div className={styles.colorDot} style={{ backgroundColor: '#ffffff' }} />
                       <div className={styles.colorDot} style={{ backgroundColor: '#1890ff' }} />
                     </>
                   )}
                 </div>
              </div>
              );
            })}
          </div>

          <div className={styles.resetButton}>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleResetToDefault}
              type="dashed"
              size="small"
            >
              {intl.formatMessage({ id: 'app.theme.reset', defaultMessage: '跟随浏览器默认值' })}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ThemeSelector;